import { Chip, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { format } from "date-fns";

import { PublicHolidayKind, PublicHolidayResponse } from "@/api";
import { DataTable } from "@/common/components/table/DataTable";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

interface Props {
  holidays: PublicHolidayResponse[];
  isLoading?: boolean;
}

export function HolidayTable({ holidays, isLoading }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const columns: Array<GridColDef<PublicHolidayResponse>> = [
    {
      field: "date",
      headerName: t("settings.holidays.table.date"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<PublicHolidayResponse>) =>
        format(params.row.date, "dd.MM.yyyy", { locale: locales[language] }),
    },
    {
      field: "weekday",
      headerName: t("settings.holidays.table.weekday"),
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PublicHolidayResponse>) =>
        format(params.row.date, "EEEE", { locale: locales[language] }),
    },
    {
      field: "name",
      headerName: t("settings.holidays.table.name"),
      flex: 2,
    },
    {
      field: "kind",
      headerName: t("settings.holidays.table.type"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<PublicHolidayResponse, PublicHolidayKind>) => {
        const isDienstfrei = params.value === PublicHolidayKind.Dienstfrei;
        return (
          <Chip
            label={isDienstfrei ? t("settings.holidays.kind.dienstfrei") : t("settings.holidays.kind.publicHoliday")}
            color={isDienstfrei ? "default" : "primary"}
            variant={isDienstfrei ? "outlined" : "filled"}
            size="small"
          />
        );
      },
    },
  ];

  if (!isLoading && holidays.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {t("settings.holidays.empty")}
      </Typography>
    );
  }

  return (
    <DataTable
      data={holidays}
      columns={columns}
      isLoading={isLoading}
      getRowId={(row) => row.id}
      onRowClick={() => {}}
      rowHeight={56}
    />
  );
}
